import { Clock3, FileText, Loader2 } from 'lucide-react'
import { Suspense, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LoadingState } from '~/components/common/LoadingSpinner'
import PaginatedMarkdown from '~/components/documents/PaginatedDocMarkdown'
import {
  DownloadDocumentDropdown,
  DownloadDropdownPlaceholder,
  preprocessContent
} from '~/components/task/DocsPanel'
import {
  LandingTaskPanelBody,
  LandingTaskPanelFooter,
  LandingTaskPanelFrame,
  LandingTaskPanelHeader,
  LandingTaskPanelNotice,
  LandingTaskPanelTab,
  LandingTaskPanelTabs,
  LandingTaskStatusPill
} from '~/components/task/LandingTaskPanelShell'
import LandingTaskProgressTimeline from '~/components/task/LandingTaskProgressTimeline'
import {
  RIGHT_PANEL_MARKDOWN_CLASS,
  RightPanelHorizontalScroll
} from '~/components/task/RightPanelHorizontalScroll'
import { TaskStatuses } from '~/constants/task-status'
import { TaskDetails, useTaskDetails } from '~/hooks/task/useTaskDetails'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { TaskObjectWithNoDetails } from '~/types/TaskType'
import { getEffectiveTaskStatus, normalizeCommandType } from '~/task/commandTypeUtils'
import { cm } from '~/utils'

type LandingDocumentationTab = 'progress' | 'document'

interface LandingDocumentationTaskPanelProps {
  conversationUuid: string
  onClose?: () => void
  openRequestId?: number
  task: TaskObjectWithNoDetails
}

const DOCUMENTATION_COMMANDS: ReadonlySet<CommandType> = new Set([
  CommandType.GENERATE_DOCUMENTATION
])

function formatStatus(taskStatus: AgentTaskStatus) {
  const config = TaskStatuses[taskStatus]
  if (config?.label) return config.label
  switch (config?.group) {
    case 'IN_PROGRESS':
      return 'Running'
    case 'COMPLETED':
      return 'Completed'
    case 'ERRORED':
      return 'Errored'
    case 'STOPPED':
      return 'Stopped'
    default:
      return 'Task'
  }
}

function statusGroupForTaskStatus(taskStatus: AgentTaskStatus) {
  return TaskStatuses[taskStatus]?.group
}

function extractDocumentFromTuple(tuple: TaskStatusAndRequestResponseTuple | null): string {
  if (!tuple?.response?.length) return ''

  for (let i = tuple.response.length - 1; i >= 0; i -= 1) {
    const responseContent = tuple.response[i]?.responseContentStream
    const document = responseContent?.document
    const docContent =
      document?.flowDocMdTxt ||
      document?.fullRepoMd ||
      document?.mappingSheetMd ||
      document?.dwlMd ||
      responseContent?.migrationDocOutput?.docMd ||
      ''
    if (docContent.trim()) return docContent
  }

  return ''
}

function findDocument(tuples: TaskStatusAndRequestResponseTuple[]) {
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const content = extractDocumentFromTuple(tuples[i])
    if (content) return content
  }
  return ''
}

function resolveFlowLabel(taskDetails: TaskDetails | undefined): string {
  const commandString = taskDetails?.task?.details?.originalRequest?.command?.commandString || ''
  if (!commandString.trim()) return 'Generated documentation'
  const flows = commandString
    .split(',')
    .map(flow => flow.trim())
    .filter(Boolean)
  if (flows.length === 0) return 'Generated documentation'
  if (flows.length === 1) return flows[0]
  return `${flows.length} flows`
}

interface DocumentHeading {
  id: string
  level: number
  text: string
}

function slugifyHeading(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[`*_#[\]()]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'section'
}

function extractDocumentHeadings(markdown: string, taskId: number): DocumentHeading[] {
  const headings: DocumentHeading[] = []
  const seen = new Map<string, number>()

  for (const line of markdown.split(/\r\n|\r|\n/)) {
    const match = line.match(/^(#{1,3})\s+(.+?)\s*#*\s*$/)
    if (!match) continue

    const text = match[2].replace(/[`*_]/g, '').trim()
    if (!text) continue

    const base = `landing-doc-${taskId}-${slugifyHeading(text)}`
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    headings.push({
      id: count === 0 ? base : `${base}-${count + 1}`,
      level: match[1].length,
      text
    })
  }

  return headings.slice(0, 12)
}

function addHeadingAnchors(markdown: string, headings: DocumentHeading[]): string {
  if (headings.length === 0) return markdown
  let headingIndex = 0
  return markdown
    .split(/\r\n|\r|\n/)
    .map(line => {
      const match = line.match(/^(#{1,3})\s+(.+?)\s*#*\s*$/)
      if (!match) return line
      const heading = headings[headingIndex]
      headingIndex += 1
      if (!heading) return line
      return `${line}\n<span id="${heading.id}"></span>`
    })
    .join('\n')
}

export function isLandingDocumentationTask(task: TaskObjectWithNoDetails | undefined): boolean {
  if (!task) return false
  return DOCUMENTATION_COMMANDS.has(normalizeCommandType(task.cmd_type))
}

export default function LandingDocumentationTaskPanel({
  conversationUuid,
  onClose,
  openRequestId,
  task
}: LandingDocumentationTaskPanelProps) {
  const taskId = task.task_id
  const fallbackStatus = agentTaskStatusFromJSON(task.status)
  const taskDetailsQuery = useTaskDetails(conversationUuid, String(taskId))
  const {
    data: taskDetails,
    isLoading: taskDetailsLoading,
    refetch: refetchTaskDetails
  } = taskDetailsQuery

  const tuples = useMemo(
    () =>
      (taskDetails?.task?.details?.orderedStateResponses ??
        []) as TaskStatusAndRequestResponseTuple[],
    [taskDetails?.task?.details?.orderedStateResponses]
  )
  const detailStatus = taskDetails?.task?.status
    ? agentTaskStatusFromJSON(taskDetails.task.status)
    : AgentTaskStatus.NONE_TASK_STATUS
  const rawTaskStatus =
    detailStatus !== AgentTaskStatus.NONE_TASK_STATUS ? detailStatus : fallbackStatus
  const taskStatus = getEffectiveTaskStatus(rawTaskStatus, normalizeCommandType(task.cmd_type))
  const statusGroup = TaskStatuses[taskStatus]?.group
  const isRunning = statusGroup === 'IN_PROGRESS'
  const { data: progressMessages = [] } = useTaskProgressMessages(
    conversationUuid,
    String(taskId),
    {
      enabled: !!conversationUuid && !!taskId,
      refetchInterval: isRunning ? 10000 : false
    }
  )
  const documentMarkdown = useMemo(() => findDocument(tuples), [tuples])
  const documentHeadings = useMemo(
    () => extractDocumentHeadings(documentMarkdown, taskId),
    [documentMarkdown, taskId]
  )
  const documentMarkdownWithAnchors = useMemo(
    () => addHeadingAnchors(documentMarkdown, documentHeadings),
    [documentHeadings, documentMarkdown]
  )
  const flowLabel = useMemo(() => resolveFlowLabel(taskDetails), [taskDetails])
  const defaultTab: LandingDocumentationTab =
    !isRunning && documentMarkdown ? 'document' : 'progress'
  const [activeTab, setActiveTab] = useState<LandingDocumentationTab>(defaultTab)
  const effectiveActiveTab: LandingDocumentationTab = isRunning ? activeTab : 'document'
  const documentAutoSelectedRef = useRef(false)
  const [sectionScrollRequest, setSectionScrollRequest] = useState<{
    id: string
    request: number
  } | null>(null)

  useEffect(() => {
    documentAutoSelectedRef.current = false
    setSectionScrollRequest(null)
    setActiveTab(defaultTab)
  }, [taskId, openRequestId, defaultTab])

  useEffect(() => {
    if (isRunning) {
      documentAutoSelectedRef.current = false
      setActiveTab('progress')
      return
    }

    if (documentMarkdown && !documentAutoSelectedRef.current) {
      documentAutoSelectedRef.current = true
      setActiveTab('document')
    }
  }, [documentMarkdown, isRunning])

  useEffect(() => {
    refetchTaskDetails()
  }, [task.status, task.updated_ts, refetchTaskDetails])

  useEffect(() => {
    if (!isRunning) return
    const intervalId = window.setInterval(() => {
      refetchTaskDetails()
    }, 5000)
    return () => window.clearInterval(intervalId)
  }, [isRunning, refetchTaskDetails])

  const handleSectionClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, headingId: string) => {
      event.preventDefault()
      setSectionScrollRequest(current => ({
        id: headingId,
        request: (current?.request ?? 0) + 1
      }))
    },
    []
  )

  const tabs: Array<LandingTaskPanelTab<LandingDocumentationTab>> = [
    ...(isRunning
      ? [
          {
            id: 'progress' as const,
            label: 'Progress',
            icon: <Clock3 className='h-4 w-4' />
          }
        ]
      : []),
    {
      id: 'document',
      label: 'Document',
      icon: <FileText className='h-4 w-4' />,
      disabled: !documentMarkdown
    }
  ]
  const documentDownloadAction = documentMarkdown ? (
    <Suspense fallback={DownloadDropdownPlaceholder}>
      <DownloadDocumentDropdown
        content={documentMarkdown}
        filename={`documentation-task-${taskId}`}
        label='Download Options'
        variant='toolbar'
      />
    </Suspense>
  ) : null

  const tabContent = () => {
    if (!taskDetails && taskDetailsLoading) {
      return <LoadingState message='Loading documentation task...' className='h-full' />
    }

    if (effectiveActiveTab === 'progress') {
      return (
        <RightPanelHorizontalScroll testId='landing-documentation-progress-scroll'>
          <LandingTaskProgressTimeline
            emptyDescription='The documentation agent has started, but no progress checkpoints have arrived yet.'
            emptyTitle={
              isRunning ? 'Waiting for documentation progress' : 'No documentation progress yet'
            }
            progressMessages={progressMessages}
            isRunning={isRunning}
          />
        </RightPanelHorizontalScroll>
      )
    }

    return documentMarkdown ? (
      <RightPanelHorizontalScroll
        className='p-4 text-sm'
        testId='landing-documentation-document-scroll'
      >
        <div className='flex min-w-[680px] items-start gap-4'>
          {documentHeadings.length >= 2 && (
            <nav
              className='sticky top-0 max-h-[calc(100vh-220px)] w-44 shrink-0 overflow-auto border-r border-tokens-neutral-border-2 pr-3 text-xs'
              aria-label='Document sections'
              data-testid='landing-documentation-toc'
            >
              <div className='mb-2 font-semibold text-tokens-neutral-text-primary'>Sections</div>
              <div className='space-y-1'>
                {documentHeadings.map(heading => (
                  <a
                    key={heading.id}
                    href={`#${heading.id}`}
                    onClick={event => handleSectionClick(event, heading.id)}
                    className={cm(
                      'block rounded-lg px-2 py-1 text-tokens-neutral-text-secondary hover:bg-tokens-neutral-fill-fill-4 hover:text-tokens-neutral-text-primary',
                      heading.level === 2 && 'pl-4',
                      heading.level === 3 && 'pl-6'
                    )}
                  >
                    {heading.text}
                  </a>
                ))}
              </div>
            </nav>
          )}
          <div className='min-w-[520px] flex-1'>
            <PaginatedMarkdown
              text={preprocessContent(documentMarkdownWithAnchors)}
              className={RIGHT_PANEL_MARKDOWN_CLASS}
              paginationKey={`landing-documentation-${taskId}`}
              scrollTargetId={sectionScrollRequest?.id ?? null}
              scrollTargetRequest={sectionScrollRequest?.request ?? 0}
            />
          </div>
        </div>
      </RightPanelHorizontalScroll>
    ) : (
      <div className='flex h-full items-center justify-center p-6 text-center text-tokens-neutral-text-tertiary'>
        Generated documentation will appear here when the task finishes.
      </div>
    )
  }

  return (
    <LandingTaskPanelFrame>
      <LandingTaskPanelHeader
        onClose={onClose}
        title={`Documentation task #${taskId}`}
        subtitle={flowLabel}
        actions={
          <div
            className='flex shrink-0 flex-wrap items-center justify-end gap-2'
            data-testid='landing-documentation-header-actions'
          >
            {documentDownloadAction}
            <LandingTaskStatusPill
              group={statusGroupForTaskStatus(taskStatus)}
              label={formatStatus(taskStatus)}
            />
          </div>
        }
      />

      {isRunning && (
        <LandingTaskPanelTabs activeTab={effectiveActiveTab} onChange={setActiveTab} tabs={tabs} />
      )}

      <LandingTaskPanelBody>{tabContent()}</LandingTaskPanelBody>

      <LandingTaskPanelFooter>
        {documentMarkdown ? (
          <div className='flex items-center justify-between gap-3'>
            <div className='min-w-0 text-sm text-tokens-neutral-text-secondary'>
              Request document edits in chat. The assistant will work from task #{taskId}.
            </div>
          </div>
        ) : isRunning ? (
          <LandingTaskPanelNotice icon={<Loader2 className='h-4 w-4 shrink-0 animate-spin' />}>
            Documentation is running.
          </LandingTaskPanelNotice>
        ) : (
          <div className='text-sm text-tokens-neutral-text-secondary'>
            Documentation is not available yet.
          </div>
        )}
      </LandingTaskPanelFooter>
    </LandingTaskPanelFrame>
  )
}
